import {
  entityToIndex,
  ReferenceHrefBuilders,
} from '@squareup/dex-tech-utils-routing';
import { Box } from '@squareup/dex-ui-shared-base';
import { breakHtmlForCamelCaseReact } from '@squareup/dex-utils-text';
import React from 'react';

import { ReferenceItem, ReferenceItemProps } from '../../ReferenceItem';

import styles from './entity-lists.module.css';

type Entity = {
  name: string;
  id: string;
  isIndependent: boolean;
  isEnum: boolean;
} & ReferenceItemProps;

const Entities = ({
  entities,
  currentVersion,
}: {
  entities: Entity[];
  currentVersion: string;
}) => {
  const hrefBuilder = (entity: Entity) => {
    if (entity.isIndependent) {
      return ReferenceHrefBuilders.versionObject({
        object: entity.name,
        square_version: currentVersion,
      });
    } else {
      return ReferenceHrefBuilders.versionEnum({
        enum: entity.name,
        square_version: currentVersion,
      });
    }
  };

  return (
    <>
      {entities.map((entity) => {
        const subItems = [
          {
            item: {
              // `entity` contains getters which cannot be spread, so instead,
              // required properties are explicitly specified below:
              description: entity.description,
              httpMethod: entity.httpMethod,
              id: entity.id,
              isBeta: entity.isBeta,
              isDeprecated: entity.isDeprecated,
              isV1: entity.isV1,
              name: entity.name,
              path: entity.path,
              summary: entity.summary,
              title: breakHtmlForCamelCaseReact(entity.name),
            },
            className: styles['reference-item'],
            as: 'li',
            key: entity.name,
            href: hrefBuilder(entity),
          },
        ];

        return <ReferenceItem key={entity.id} subItems={subItems} />;
      })}
    </>
  );
};

interface EntityListsProps {
  lettersEntitiesMap: Map<string, Entity[]>;
  type: 'object' | 'enum';
  setListRef: (ref: HTMLElement | null) => void;
  currentVersion: string;
}

const EntityLists = React.memo(
  ({
    lettersEntitiesMap,
    type,
    setListRef,
    currentVersion,
  }: EntityListsProps) => {
    if (lettersEntitiesMap.size === 0) {
      return null;
    }

    return (
      <div ref={setListRef}>
        {[...lettersEntitiesMap.entries()].map(([letter, entities]) => {
          const id = entityToIndex(type, letter);
          return entities ? (
            // Add an id to the div to allow for anchor scrolling by the browser
            <div key={id} id={id}>
              <Box margin={{ top: '3x' }}>
                <ul key={letter}>
                  <Entities
                    entities={entities}
                    currentVersion={currentVersion}
                  />
                </ul>
              </Box>
            </div>
          ) : null;
        })}
      </div>
    );
  }
);

EntityLists.displayName = 'EntityLists';

export { EntityLists, type Entity };
